'use client';

import { Table } from '@tanstack/react-table';
import { Plus, RefreshCw, Upload } from 'lucide-react';
import * as React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Funnel } from '@/lib/api/funnels';

interface FunnelsTableActionsProps {
  table: Table<Funnel>;
  onRefresh?: () => void;
  onCreate?: () => void;
  onImport?: (file: File) => void;
  isLoading?: boolean;
  isImporting?: boolean;
}

/**
 * Actions toolbar for funnels table
 */
export function FunnelsTableActions({ table, onRefresh, onCreate, onImport, isLoading, isImporting }: FunnelsTableActionsProps) {
  const { t } = useTranslation('common')
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  // Handle refresh
  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh()
      toast.success(t('funnelsTable.toasts.refreshed'))
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading}>
        <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
        {t('funnelsTable.actions.refresh')}
      </Button>
      {onImport && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0]
              event.target.value = ''
              if (file) onImport(file)
            }}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading || isImporting}
          >
            <Upload className="mr-2 h-4 w-4" />
            {t('funnelsTable.actions.importBundle')}
          </Button>
        </>
      )}
      {onCreate && (
        <Button size="sm" onClick={onCreate}>
          <Plus className="mr-2 h-4 w-4" />
          {t('funnelsTable.actions.createFunnel')}
        </Button>
      )}
    </div>
  )
}
